
// Advanced sentiment analysis utilities with browser compatibility
import { 
  detectLanguage, 
  normalizeText,
  tokenizeByLanguage,
  clusterFeedback as clusterFeedbackUtil,
  analyzeResponseFeedbackCorrelation as analyzeResponseFeedbackCorrelationUtil
} from './simple-multilingual';

export interface SentimentResult {
  score: number;
  comparative: number;
  positive: string[];
  negative: string[];
  keyPhrases: string[];
  language: string;
}

/**
 * Analyzes text to determine sentiment score and extract key aspects
 * Enhanced with multilingual support, especially for Vietnamese
 * @param text Text content to analyze
 * @returns Sentiment analysis result
 */
export function analyzeText(text: string): SentimentResult {
  // First detect the language
  const language = detectLanguage(text);
  const normalizedText = normalizeText(text);
  
  // Get the appropriate word lists based on language
  const { positiveWords, negativeWords } = getSentimentWordsByLanguage(language);
  
  // Match found words - preserve diacritics for Vietnamese
  const foundPositive = positiveWords.filter(word => {
    const pattern = language === 'vi' 
      ? new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i')
      : new RegExp(`\\b${word}\\b`, 'i');
    return pattern.test(normalizedText);
  });
  
  const foundNegative = negativeWords.filter(word => {
    const pattern = language === 'vi'
      ? new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i')
      : new RegExp(`\\b${word}\\b`, 'i');
    return pattern.test(normalizedText);
  });
  
  // Calculate sentiment score
  const positiveScore = foundPositive.length;
  const negativeScore = foundNegative.length;
  const totalWords = tokenizeByLanguage(normalizedText, language).length || 1;
  const sentimentScore = (positiveScore - negativeScore) / Math.sqrt(totalWords);
  const comparative = (positiveScore - negativeScore) / totalWords;
  
  // Extract key phrases based on language
  const keyPhrases = extractKeyPhrases(normalizedText, language, foundPositive, foundNegative);
  
  return {
    score: Math.max(Math.min(sentimentScore, 1), -1), // Clamp between -1 and 1
    comparative,
    positive: foundPositive,
    negative: foundNegative,
    keyPhrases,
    language
  };
}

// Helper to escape special characters in regex
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Get appropriate sentiment words by language
function getSentimentWordsByLanguage(language: string): { positiveWords: string[], negativeWords: string[] } {
  // English sentiment words
  const enPositive = [
    'good', 'great', 'excellent', 'amazing', 'wonderful', 'fantastic',
    'helpful', 'impressive', 'useful', 'easy', 'love', 'best', 'perfect',
    'clear', 'awesome', 'outstanding', 'brilliant', 'exceptional',
    'thank', 'thanks', 'appreciate', 'well', 'happy', 'satisfied'
  ];
  
  const enNegative = [
    'bad', 'poor', 'terrible', 'awful', 'horrible', 'useless',
    'difficult', 'confusing', 'unclear', 'wrong', 'error', 'issue',
    'problem', 'fail', 'disappointed', 'frustrating', 'complicated',
    'slow', 'broken', 'misleading', 'buggy', 'annoying', 'waste'
  ];
  
  // Vietnamese sentiment words
  const viPositive = [
    'tốt', 'hay', 'tuyệt', 'tuyệt vời', 'xuất sắc', 'hữu ích', 'dễ dàng', 
    'đơn giản', 'rõ ràng', 'thích', 'yêu thích', 'cảm ơn', 'cám ơn', 'hài lòng',
    'đẹp', 'hiệu quả', 'chính xác', 'đúng', 'nhanh', 'tuyệt hảo', 'giỏi'
  ];
  
  const viNegative = [
    'kém', 'tệ', 'tồi', 'khó', 'phức tạp', 'không rõ', 'sai', 'lỗi', 'vấn đề',
    'thất vọng', 'chậm', 'khó chịu', 'không hiệu quả', 'không chính xác',
    'không đúng', 'lãng phí', 'hỏng', 'không hài lòng', 'thất bại',
    'khó hiểu', 'không hữu ích', 'không thể', 'không hoạt động'
  ];
  
  // Add more languages as needed
  
  switch(language) {
    case 'vi':
      return { positiveWords: viPositive, negativeWords: viNegative };
    default:
      return { positiveWords: enPositive, negativeWords: enNegative };
  }
}

// Extract key phrases based on language
function extractKeyPhrases(text: string, language: string, positiveWords: string[], negativeWords: string[]): string[] {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  
  // Identify sentences containing sentiment words
  const sentimentSentences = sentences
    .filter(s => {
      const lowerS = s.toLowerCase();
      return positiveWords.some(w => lowerS.includes(w)) || 
             negativeWords.some(w => lowerS.includes(w));
    })
    .map(s => s.trim())
    .slice(0, 3); // Take up to 3 key phrases
  
  return sentimentSentences;
}

/**
 * Analyzes text to extract emotion intensities
 * Enhanced with multilingual support
 * @param text Text content to analyze
 * @returns Object mapping emotions to their intensities (0-1 scale)
 */
export function analyzeEmotions(text: string): Record<string, number> {
  const language = detectLanguage(text);
  const lowercaseText = text.toLowerCase();
  
  // Define emotion keywords and their associated weights for different languages
  const emotionWords: Record<string, Record<string, string[]>> = {
    en: {
      joy: ['happy', 'excited', 'delighted', 'pleased', 'glad', 'cheerful', 'joy', 'love', 'wonderful', 'great'],
      anger: ['angry', 'mad', 'frustrated', 'annoyed', 'irritated', 'furious', 'rage', 'hostile', 'bitter'],
      sadness: ['sad', 'unhappy', 'disappointed', 'depressed', 'miserable', 'gloomy', 'heartbroken', 'regret'],
      fear: ['afraid', 'scared', 'frightened', 'anxious', 'worried', 'nervous', 'terrified', 'panic', 'concerned'],
      surprise: ['surprised', 'shocked', 'amazed', 'astonished', 'unexpected', 'startled', 'wow', 'sudden'],
      trust: ['trust', 'believe', 'reliable', 'depend', 'confident', 'faith', 'assurance', 'credible']
    },
    vi: {
      joy: ['vui', 'hạnh phúc', 'phấn khởi', 'thích', 'hài lòng', 'vui vẻ', 'thú vị', 'yêu', 'tuyệt vời', 'tốt'],
      anger: ['giận', 'tức giận', 'bực', 'khó chịu', 'cáu', 'phẫn nộ', 'gắt gỏng', 'khó ưa'],
      sadness: ['buồn', 'không vui', 'thất vọng', 'chán nản', 'đau buồn', 'u sầu', 'đau khổ', 'tiếc'],
      fear: ['sợ', 'lo', 'lo lắng', 'hoảng sợ', 'lo ngại', 'bồn chồn', 'kinh hoàng', 'hoảng loạn'],
      surprise: ['bất ngờ', 'sốc', 'ngạc nhiên', 'kinh ngạc', 'không ngờ', 'giật mình', 'bàng hoàng'],
      trust: ['tin', 'tin tưởng', 'đáng tin cậy', 'phụ thuộc', 'tự tin', 'niềm tin', 'tin cậy', 'đáng tin']
    }
    // Add more languages as needed
  };
  
  // Use English as default if the language is not supported
  const currentEmotionWords = emotionWords[language] || emotionWords.en;
  
  // Calculate emotion scores
  const emotions: Record<string, number> = {};
  const tokens = tokenizeByLanguage(text, language);
  const totalTokens = tokens.length || 1;
  
  Object.entries(currentEmotionWords).forEach(([emotion, words]) => {
    let count = 0;
    
    words.forEach(word => {
      // For Vietnamese, need to handle word boundaries carefully
      if (language === 'vi') {
        // Check for word as part of phrases or whole words
        if (lowercaseText.includes(word)) {
          count++;
        }
      } else {
        const regex = new RegExp(`\\b${word}\\b`, 'i');
        if (regex.test(lowercaseText)) {
          count++;
        }
      }
    });
    
    // Normalize score between 0 and 1
    emotions[emotion] = Math.min(count / (totalTokens * 0.2), 1);
  });
  
  // Ensure all emotions have a value
  Object.keys(currentEmotionWords).forEach(emotion => {
    if (emotions[emotion] === undefined) {
      emotions[emotion] = 0;
    }
  });
  
  return emotions;
}

/**
 * Re-export the browser-compatible functions from simple-multilingual
 */
export const clusterFeedback = clusterFeedbackUtil;
export const analyzeResponseFeedbackCorrelation = analyzeResponseFeedbackCorrelationUtil;
